import React, { useState, useEffect } from "react";
import Select from "react-select";
import { postAdminBroadcast } from "../../../../api/system/notifications";
import { getAllEmployeesIsActive } from "../../../../api/hrm/employees/employeeInfo";
import { getAllBranches_Array } from "../../../../api/hrm/branches/info";
import { getAllDepartments } from "../../../../api/hrm/departments/departmentInfo";
import { getRoles } from "../../../../api/settings/roleManager";
import customSelectStyles from "../../../../components/ui/customSelectStyles";
import { showSuccessToast, showErrorToast } from "../../../../components/ConfirmDialog";

function hasAnyTarget(t) {
  return (
    t.include_all_employees ||
    t.include_teachers ||
    t.include_students ||
    (t.branch_ids && t.branch_ids.length > 0) ||
    (t.department_ids && t.department_ids.length > 0) ||
    (t.role_ids && t.role_ids.length > 0) ||
    (t.employee_ids && t.employee_ids.length > 0)
  );
}

export default function NotificationCreateModal({ onClose, onSuccess }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("system.admin");
  const [message, setMessage] = useState("");
  const [mandatoryModal, setMandatoryModal] = useState(true);
  const [minDisplaySeconds, setMinDisplaySeconds] = useState(10);
  const [maintenanceLock, setMaintenanceLock] = useState(false);
  const [includeAllEmployees, setIncludeAllEmployees] = useState(false);
  const [includeTeachers, setIncludeTeachers] = useState(false);
  const [includeStudents, setIncludeStudents] = useState(false);
  const [branchIdsSelected, setBranchIdsSelected] = useState([]);
  const [departmentIdsSelected, setDepartmentIdsSelected] = useState([]);
  const [roleIdsSelected, setRoleIdsSelected] = useState([]);
  const [receivers, setReceivers] = useState([]);
  const [branchRows, setBranchRows] = useState([]);
  const [departmentRows, setDepartmentRows] = useState([]);
  const [roleRows, setRoleRows] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [sending, setSending] = useState(false);

  const categoryOptions = [
    { value: "system", label: "Hệ thống" },
    { value: "hrm", label: "Nhân sự (HRM)" },
    { value: "crm", label: "CRM" },
    { value: "finance", label: "Tài chính" },
    { value: "elearning", label: "E-Learning" },
    { value: "system.admin", label: "Quản trị (admin)" },
    { value: "other", label: "Khác" },
  ];

  useEffect(() => {
    loadUsers();
    loadFilters();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await getAllEmployeesIsActive();
      const options = (res || []).map((u) => ({
        value: u.id,
        label: `${u.name} (${u.email})`,
      }));
      setUserOptions(options);
    } catch (err) {
      showErrorToast("Không thể tải danh sách người dùng");
    }
  };

  const loadFilters = async () => {
    try {
      const [b, d, r] = await Promise.all([
        getAllBranches_Array(),
        getAllDepartments(),
        getRoles(),
      ]);
      setBranchRows(Array.isArray(b) ? b : []);
      setDepartmentRows(Array.isArray(d) ? d : []);
      const rolesData = r?.data ?? r ?? [];
      setRoleRows(Array.isArray(rolesData) ? rolesData : []);
    } catch (err) {
      console.error(err);
    }
  };

  const branchOptions = branchRows.map((x) => ({
    value: x.id,
    label: x.name || x.code || String(x.id),
  }));
  const departmentOptions = departmentRows.map((x) => ({
    value: x.id,
    label: x.name || String(x.id),
  }));
  const roleOptions = roleRows.map((x) => ({
    value: x.id,
    label: x.name || String(x.id),
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      showErrorToast("Vui lòng nhập tiêu đề và nội dung");
      return;
    }

    const finalTarget = {
      include_all_employees: includeAllEmployees,
      include_teachers: includeTeachers,
      include_students: includeStudents,
      branch_ids: branchIdsSelected,
      department_ids: departmentIdsSelected,
      role_ids: roleIdsSelected,
      employee_ids: receivers.map((x) => x.value),
    };

    if (!hasAnyTarget(finalTarget)) {
      showErrorToast("Chọn ít nhất một nhóm người nhận");
      return;
    }

    const sec = Math.min(120, Math.max(3, parseInt(minDisplaySeconds, 10) || 10));

    try {
      setSending(true);
      await postAdminBroadcast({
        title: title.trim(),
        message: message.trim(),
        module_code: category || "system.admin",
        mandatory_modal: maintenanceLock ? true : mandatoryModal,
        min_display_seconds: sec,
        maintenance_lock: maintenanceLock,
        target: finalTarget,
      });

      showSuccessToast(
        maintenanceLock
          ? "Đã gửi thông báo và tắt app tạm thời!"
          : "Đã gửi thông báo thành công!"
      );
      if (maintenanceLock) {
        window.dispatchEvent(
          new CustomEvent("app-maintenance-updated", {
            detail: {
              enabled: true,
              title: title.trim(),
              message: message.trim(),
            },
          })
        );
      }
      window.dispatchEvent(new Event("refresh-notification-count"));
      onSuccess?.();
      onClose();
    } catch (err) {
      showErrorToast(err?.message || "Không thể gửi thông báo");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full shadow-xl animate-fadeIn max-h-[92vh] flex flex-col">
        <div className="p-6 border-b border-gray-100 shrink-0">
          <h2 className="text-xl font-bold">Gửi thông báo (Ban quản trị)</h2>
          <p className="text-xs text-gray-500 mt-1">
            Chọn nhóm đối tượng (cộng dồn). Bật &quot;Popup bắt buộc&quot; để hiển thị modal sau đăng nhập.
            Bật &quot;Tắt app tạm thời&quot; để khóa toàn hệ thống đến khi Ban quản trị mở lại.
            Thông báo tự động từ HRM/task vẫn dùng luồng cũ (không qua form này).
          </p>
        </div>

        <form className="space-y-4 p-6 overflow-y-auto flex-1" onSubmit={handleSubmit}>
          <div>
            <label className="font-medium text-sm">Tiêu đề</label>
            <input
              type="text"
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Tiêu đề thông báo"
            />
          </div>

          <div>
            <label className="font-medium text-sm">Phân loại (module_code)</label>
            <Select
              styles={customSelectStyles}
              options={categoryOptions}
              value={categoryOptions.find((o) => o.value === category)}
              onChange={(opt) => setCategory(opt?.value || "system.admin")}
              placeholder="Chọn phân loại..."
            />
          </div>

          <div>
            <label className="font-medium text-sm">Nội dung</label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 mt-1"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Nội dung chi tiết..."
            />
          </div>

          <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={maintenanceLock ? true : mandatoryModal}
                  disabled={maintenanceLock}
                  onChange={(e) => setMandatoryModal(e.target.checked)}
                />
                <span className="font-medium">Popup bắt buộc khi đăng nhập</span>
              </label>
              <div>
                <label className="text-xs text-gray-600">Thời gian tối thiểu (3–120 giây)</label>
                <input
                  type="number"
                  min={3}
                  max={120}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={minDisplaySeconds}
                  onChange={(e) => setMinDisplaySeconds(e.target.value)}
                />
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={maintenanceLock}
                onChange={(e) => {
                  const on = e.target.checked;
                  setMaintenanceLock(on);
                  if (on) {
                    setMandatoryModal(true);
                    setIncludeAllEmployees(true);
                  }
                }}
              />
              <span>
                <span className="font-medium text-red-700">Tắt app tạm thời (bảo trì)</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Khóa toàn bộ người dùng đến khi tài khoản quản trị hệ thống mở lại tại trang này.
                </span>
              </span>
            </label>
          </div>

          <div>
            <p className="font-semibold text-sm mb-2">Đối tượng nhận (OR — hợp các nhóm)</p>
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeAllEmployees}
                  onChange={(e) => setIncludeAllEmployees(e.target.checked)}
                />
                Toàn bộ nhân viên có phân quyền
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeTeachers}
                  onChange={(e) => setIncludeTeachers(e.target.checked)}
                />
                Giáo viên (theo tên vai trò)
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeStudents}
                  onChange={(e) => setIncludeStudents(e.target.checked)}
                />
                Học viên hoạt động (student_info.id)
              </label>
            </div>
          </div>

          <div>
            <label className="font-medium text-sm">Chi nhánh</label>
            <Select
              isMulti
              styles={customSelectStyles}
              options={branchOptions}
              value={branchOptions.filter((o) => branchIdsSelected.includes(o.value))}
              onChange={(opts) => setBranchIdsSelected((opts || []).map((o) => o.value))}
              placeholder="Chọn chi nhánh..."
            />
          </div>

          <div>
            <label className="font-medium text-sm">Bộ phận</label>
            <Select
              isMulti
              styles={customSelectStyles}
              options={departmentOptions}
              value={departmentOptions.filter((o) => departmentIdsSelected.includes(o.value))}
              onChange={(opts) => setDepartmentIdsSelected((opts || []).map((o) => o.value))}
              placeholder="Chọn bộ phận..."
            />
          </div>

          <div>
            <label className="font-medium text-sm">Vai trò</label>
            <Select
              isMulti
              styles={customSelectStyles}
              options={roleOptions}
              value={roleOptions.filter((o) => roleIdsSelected.includes(o.value))}
              onChange={(opts) => setRoleIdsSelected((opts || []).map((o) => o.value))}
              placeholder="Chọn vai trò..."
            />
          </div>

          <div>
            <label className="font-medium text-sm">Thêm từng nhân viên</label>
            <Select
              isMulti
              styles={customSelectStyles}
              options={userOptions}
              value={receivers}
              onChange={(val) => setReceivers(val || [])}
              placeholder="Người nhận cụ thể..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              className="px-4 py-2 rounded-lg border border-gray-200"
              onClick={onClose}
              disabled={sending}
            >
              Đóng
            </button>
            <button
              type="submit"
              disabled={sending}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {sending ? "Đang gửi…" : "Gửi thông báo"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
